import { IcServer, IcSoftware } from '@assets/icons/40x40';
import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import DataSheetCard from '@components/common/layout/DataSheetCard';
import { ToastStatusEnum } from '@components/ui/toaster';
import { usePostHolodroneCommandMutation } from '@services/c2/c2.api';
import { createSensorToast } from '@utils/sensors/sensors.utils';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { HolodroneCommandEnum } from '@/types/c2/c2.types';
import { SensorTypeEnum } from '@/types/sensor/sensors';

type Props = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfoByType<SensorTypeEnum.HOLODRONE>;
  replayMode: boolean;
};

export default function HolodroneMaintenanceActions({ sensorOrSubSensorInfo, replayMode }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const [postHolodroneCommand] = usePostHolodroneCommandMutation();
  const [loadingButton, setLoadingButton] = useState<string | null>(null); // État pour suivre quel bouton est en cours de chargement
  const { configuration } = sensorOrSubSensorInfo;
  const site = configuration.site;
  const appCode = configuration.appCode;
  const sensorCode = configuration.code;

  const handleCommand = async (commandType: HolodroneCommandEnum) => {
    setLoadingButton(commandType);
    const toastParams = {
      commandValues: { state: commandType },
      command: 'sensors.commands.holodrone.command',
      name: configuration.name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    postHolodroneCommand({
      site,
      appCode,
      command: { type: commandType, sensorCode },
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      )
      .finally(() => setLoadingButton(null));
  };

  const rebootActions = [
    {
      action: HolodroneCommandEnum.RebootHolodrone,
      i18nKey: 'sensors.tab.maintenance.holodrone.reboot',
    },
  ] as const;

  const restartActions = [
    {
      action: HolodroneCommandEnum.RestartHolodrone,
      i18nKey: 'sensors.tab.maintenance.holodrone.restart',
    },
  ] as const;

  return (
    <>
      <DataSheetCard
        icon={IcServer}
        actions={rebootActions}
        description={formatMessage({ id: 'sensors.tab.maintenance.holodrone.rebootDescription' })}
        isLoading={loadingButton === HolodroneCommandEnum.RebootHolodrone}
        isDisabled={loadingButton !== null || replayMode}
        onClick={handleCommand}
      />
      <DataSheetCard
        icon={IcSoftware}
        actions={restartActions}
        description={formatMessage({ id: 'sensors.tab.maintenance.holodrone.restartDescription' })}
        isLoading={loadingButton === HolodroneCommandEnum.RestartHolodrone}
        isDisabled={loadingButton !== null || replayMode}
        onClick={handleCommand}
      />
    </>
  );
}
